// backoffice/src/lib/useCrypto.ts
// Hook React : récupère la clé Fernet du praticien et fournit un helper decrypt()

import { useEffect, useState, useCallback } from "react";
import { supabase } from "./supabase";
import { decryptFernet } from "./fernetUtils";
import { useUser } from "./UserContext";

interface UseKey {
  key: string | null;
  decrypt: (token: string) => Promise<string>;
}

/* URL de l’API FastAPI (Render)  ----------------------------------- */
const API_URL =
  import.meta.env.VITE_API_URL || "https://whatsapp-healthcare.onrender.com";

export function usePracticienKey(): UseKey {
  const user = useUser();                      // undefined | null | UserProfile
  const [key, setKey] = useState<string | null>(null);

  /* Chargement de la clé dès que le praticien est connecté --------- */
  useEffect(() => {
    // 🔄 Ne rien faire tant que le contexte est "undefined" (en cours de chargement)
    if (user === undefined) return;
  
    if (user === null || user.role !== "praticien") {
      setKey(null);
      return;
    }
  
    (async () => {
      const { data } = await supabase.auth.getSession();
      const session = data.session;
      if (!session) {
        setKey(null);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/key`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });

        if (res.ok) {
          const { key } = await res.json();
          setKey(key);
        } else {
          console.warn("/key request failed", res.status);
        }
      } catch (err) {
        console.error("Erreur réseau /key :", err);
      }
    })();
  }, [user]);

  /* Helper de déchiffrement --------------------------------------- */
  const decrypt = useCallback(
    async (token: string) => {
      if (!key) throw new Error("Key not loaded");
      return decryptFernet(key, token);
    },
    [key]
  );

  return { key, decrypt };
}
